import { externalLinks } from './site'

// Recurring BCM activities — the editable source for "A typical week at BCM".
//
// HOW TO MAINTAIN THIS FILE
// Set `visible: true` only after BCM leadership confirms the day, time, and
// location. Public components read `visibleRecurring`, never the raw array, so
// an unconfirmed record cannot reach cards, labels, tooltips, or screen readers.
//
// TODO (content): Confirm whether the August 31 Women’s Community Group begins
// at 7:00 PM or 7:30 PM before publishing it as an individual event. The general
// card below stays at the confirmed 7:00 PM weekly time until then.

export type ActivityLocation = {
  /** Optional group name, e.g. "Women’s Community Group". */
  label?: string
  text: string
}

export type RecurringActivity = {
  id: string
  activity: string
  /** Weekday, e.g. "Thursday". Only rendered when visible. */
  day: string
  /** Time, e.g. "7:30 PM". Only rendered when visible. */
  time: string
  locations: ActivityLocation[]
  /** Small secondary line under the locations. */
  note?: string
  description?: string
  /** Internal maintenance status. Never rendered. */
  status: string
  /** Gate for public display. */
  visible: boolean
  /** Where students should look for current details. */
  updateLink?: string
}

const recurringActivities: RecurringActivity[] = [
  {
    id: 'thursday-worship',
    activity: 'Thursday Worship Nights',
    day: 'Thursday',
    time: '7:30 PM',
    locations: [
      { text: 'Cape Florida Ballroom, Room 316' },
      { text: 'UCF Student Union, 3rd Floor' },
    ],
    description:
      'Gather for worship, a Bible-centered message, fellowship, and time to meet other students.',
    status: 'Confirmed',
    visible: true,
  },
  {
    id: 'community-groups',
    activity: 'Community Groups',
    day: 'Monday',
    time: '7:00 PM',
    locations: [
      {
        label: 'Women’s Community Group',
        text: 'Most meetings are on the second floor of the UCF Student Union near Starbucks.',
      },
      { label: 'Men’s Community Group', text: 'Location varies.' },
    ],
    note: 'Current locations are announced through BCM’s official channels.',
    description:
      'Women’s and men’s community groups meet for Scripture, conversation, friendship, and prayer.',
    status: 'Confirmed weekly time',
    visible: true,
    updateLink: externalLinks.instagram,
  },
  {
    id: 'sports-nights',
    activity: 'Sports Nights',
    day: 'Friday',
    time: '7:00 PM',
    locations: [{ text: 'Usually at Lake Claire Recreational Area' }],
    note: 'Some special sports, such as pickleball, may meet at the UCF RWC.',
    description:
      'BCM gathers throughout the semester for recreation and outreach for YOU to be able to build community during your time at UCF!',
    status: 'Confirmed',
    visible: true,
    updateLink: externalLinks.instagram,
  },
]

/** The only recurring records any public component may render. */
export const visibleRecurring: RecurringActivity[] = recurringActivities.filter(
  (activity) => activity.visible,
)

/** Cards for the "A typical week at BCM" section. */
export type TypicalWeekCard = {
  id: string
  label: string
  title: string
  /** Recurring record supplying the schedule and locations. */
  recurringId: string
  /** Optional descriptive copy shown above the schedule details. */
  copy?: string
  link?: { label: string; href: string }
}

export const typicalWeekCards: TypicalWeekCard[] = [
  {
    id: 'weekly-worship',
    label: 'Weekly Worship',
    title: 'Thursday Worship Nights',
    recurringId: 'thursday-worship',
    copy: 'Worship nights give students space to worship together, hear a Bible-centered message, and build community. Everyone is welcome, and you are always welcome to bring a friend.',
  },
  {
    id: 'community-groups',
    label: 'Grow Together',
    title: 'Community Groups',
    recurringId: 'community-groups',
    copy: 'Women’s and men’s community groups meet for Scripture, conversation, friendship, and prayer.',
  },
  {
    id: 'sports-nights',
    label: 'Community & Recreation',
    title: 'Sports Nights',
    recurringId: 'sports-nights',
    copy: 'BCM gathers throughout the semester for recreation and outreach for YOU to be able to build community during your time at UCF!',
    link: { label: 'See This Week’s Update', href: externalLinks.instagram },
  },
]

/** Lookup used by the card component; returns undefined for unconfirmed records. */
export function findVisibleRecurring(id: string) {
  return visibleRecurring.find((activity) => activity.id === id)
}
